import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("p", { class: "font-size-x-small px-4 opacity-60" }, " Enabling this creates any custom status on the appointments as a tag ", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h6", null, "Appointment Export Date Range", -1)
const _hoisted_3 = { class: "v-input v-input--density-compact v-input--dirty v-input--readonly v-text-field v-select v-select--single" }
const _hoisted_4 = { class: "input__control" }
const _hoisted_5 = { class: "v-field v-field--active v-field--appended v-field--dirty v-field--has-label v-field--variant-outlined v-theme--opendockTheme" }
const _hoisted_6 = { class: "v-field__field" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "v-field__outline" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "v-field__outline__start" }),
  /*#__PURE__*/_createElementVNode("div", { class: "v-field__outline__notch" }, [
    /*#__PURE__*/_createElementVNode("label", {
      class: "v-label v-field-label v-field-label--floating",
      "aria-hidden": "true"
    }, " Appointment Export Start Date ")
  ]),
  /*#__PURE__*/_createElementVNode("div", { class: "v-field__outline__end" })
], -1)
const _hoisted_8 = { class: "v-input v-input--density-compact v-input--dirty v-input--readonly v-text-field v-select v-select--single" }
const _hoisted_9 = { class: "input__control" }
const _hoisted_10 = { class: "v-field v-field--active v-field--appended v-field--dirty v-field--has-label v-field--variant-outlined v-theme--opendockTheme" }
const _hoisted_11 = { class: "v-field__field" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("div", { class: "v-field__outline" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "v-field__outline__start" }),
  /*#__PURE__*/_createElementVNode("div", { class: "v-field__outline__notch" }, [
    /*#__PURE__*/_createElementVNode("label", {
      class: "v-label v-field-label v-field-label--floating",
      "aria-hidden": "true"
    }, " Appointment Export End Date ")
  ]),
  /*#__PURE__*/_createElementVNode("div", { class: "v-field__outline__end" })
], -1)
const _hoisted_13 = { class: "d-flex justify-center justify-self-end" }

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { onMounted, reactive, ref, watch } from 'vue';
import { APIs } from '@/enums/APIs';
import { getClient } from '@/services/clientService';
import { GenericArrayOfObjects, INormalizedResponse } from '@/interfaces/api';
import { INovaAppointment, INovaDock, INovaLoadType, INovaWarehouse } from '@/interfaces/nova';
import { exportWorkbook, IExportedData } from '@/services/workbookService';
import { AppointmentPlusApiService } from '@/services/appointment_plus/AppointmentPlusApiService';
import {
  cleanWhiteSpace,
  EMAIL_SEPARATION_REGEX,
  flashErrorsIfExist,
  makeUniqueObjectArray,
  systemNotify
} from '@/services/util';
import { appointmentPlusUrls } from '@/clients/appointment_plus/WSClient';
import { DateTime } from 'luxon';
import { AppointmentPlusDateFormats } from '@/enums/appointmentplus/appointmentplusEnums';
import WarehouseSelectDialog from '@/components/dialogs/WarehouseSelectDialog.vue';
import ProgressDialog from '@/components/dialogs/ProgressDialog.vue';
import { useProgress } from '@/composables/progressBar';
import config from '@/config/config';


export default /*#__PURE__*/_defineComponent({
  __name: 'AppointmentPlusForm',
  setup(__props) {

const APPT_RANGE_START_DAYS_FROM_NOW = 1;
const APPT_RANGE_END_DAYS_FROM_NOW = 365;

const startDate = ref();
const endDate = ref();

const state = reactive({
  userName: '',
  password: '',
  loading: false,
  shouldShowDialog: false,
  warehouses: [] as GenericArrayOfObjects,
  useTestEmails: config.use_test_emails ? Boolean(config.use_test_emails) : true,
  selectedUrl: appointmentPlusUrls[0].value, // Use the first value
  apptRequestChunkSize: APPT_RANGE_END_DAYS_FROM_NOW,
  addCustomStatusTag: false
});

const TESTING_EMAIL_DOMAIN = '@example.com';

const progress = reactive(useProgress());

// EXPORT
const exportData = async (warehousesToExport: string[]) => {
  if (!isValid()) {
    return;
  }

  const APPT_RANGE_START_LUXON = startDate.value
    ? DateTime.fromJSDate(startDate.value).startOf('day')
    : DateTime.now().plus({ days: APPT_RANGE_START_DAYS_FROM_NOW }).startOf('day');
  const APPT_RANGE_END_LUXON = endDate.value
    ? DateTime.fromJSDate(endDate.value).endOf('day')
    : DateTime.now().plus({ days: APPT_RANGE_END_DAYS_FROM_NOW });

  state.loading = true;
  const apiService = getClient({
    api: APIs.AP,
    credentials: {
      userName: state.userName.trim(),
      password: state.password.trim()
    },
    url: state.selectedUrl
  }) as AppointmentPlusApiService;

  const exportedData: IExportedData = {
    warehouses: [],
    loadTypes: [],
    docks: [],
    appointments: []
  };

  // GET WAREHOUSES
  progress.setDialogVisibility(true);
  progress.createInterval('Fetching Warehouses', 15);
  try {
    const warehouseResponse: INormalizedResponse = await apiService.getWarehouses({
      location_id: warehousesToExport.join(',')
    });
    if (flashErrorsIfExist(warehouseResponse)) {
      abortExportProcess();
      return false;
    }
    if (state.useTestEmails) {
      warehouseResponse.data = warehouseResponse.data.map(warehouse => {
        if (!cleanWhiteSpace(warehouse.email)) {
          warehouse.email = 'warehouse_placeholder';
        }

        const newEmails = getTestEmails(warehouse.email);
        warehouse.email = newEmails?.[0] ?? '';

        return warehouse;
      });
    }

    exportedData.warehouses = warehouseResponse.data as INovaWarehouse[];
  } catch (e) {
    abortExportProcess();
    return false;
  }

  // GET DOCKS
  await progress.zoom(15);
  progress.createInterval('Fetching Docks', 40);
  try {
    const dockPromises: Promise<INormalizedResponse>[] = warehousesToExport.map(async warehouse => {
      return apiService.getDocks({
        status: 'active',
        c_id: warehouse
      });
    });

    let docks: INovaDock[] = [];
    let errors = false;

    await Promise.all(dockPromises).then(responses => {
      responses.forEach(response => {
        if (flashErrorsIfExist(response)) {
          errors = true;
          abortExportProcess();
          return false;
        }
        docks = [...docks, ...(response.data as INovaDock[])];
      });
    });

    if (errors) {
      return;
    }

    docks = makeUniqueObjectArray(docks) as INovaDock[];

    exportedData.docks = docks.filter(dock => {
      return warehousesToExport.includes(dock.externalWarehouseId);
    }) as INovaDock[];
  } catch (error: any) {
    systemNotify({
      message: error.message,
      type: 'error'
    });
    return abortExportProcess();
  }

  // GET LOAD TYPES
  await progress.zoom(40);
  progress.createInterval('Fetching Load Types', 60);
  let loadTypesArr = [] as string[];
  exportedData.docks.forEach(dock => {
    loadTypesArr = [...loadTypesArr, ...JSON.parse(dock.externalLoadTypeIds)];
  });
  const loadTypes = Array.from(new Set(loadTypesArr));
  try {
    let loadTypeObjArr: INovaLoadType[] = [];
    if (loadTypes.length > 0) {
      const loadTypesResponse: INormalizedResponse = await apiService.getLoadTypes({
        service_id: loadTypes.join(',')
      });
      if (flashErrorsIfExist(loadTypesResponse)) {
        abortExportProcess();
        return false;
      }
      loadTypeObjArr = loadTypesResponse.data as INovaLoadType[];
    }
    exportedData.loadTypes = loadTypeObjArr;
  } catch (error: any) {
    systemNotify({
      message: error.message,
      type: 'error'
    });
    return abortExportProcess();
  }

  // GET APPOINTMENTS
  await progress.zoom(60);
  progress.createInterval('Fetching Appointments', 98);
  try {
    let startIterator = APPT_RANGE_START_LUXON;
    let appointmentResponses: INormalizedResponse[] = [];
    while (startIterator <= APPT_RANGE_END_LUXON) {
      const newResponses = await Promise.all(
        warehousesToExport.map(async warehouseId => {
          const appointmentResponse: INormalizedResponse = await apiService.getAppointments(
            {
              start_date: startIterator.toFormat(AppointmentPlusDateFormats.API),
              end_date: startIterator
                .plus({ day: state.apptRequestChunkSize })
                .toFormat(AppointmentPlusDateFormats.API),
              location_id: warehouseId
            },
            exportedData.warehouses as INovaWarehouse[],
            { addCustomStatusAsTag: state.addCustomStatusTag }
          );

          return appointmentResponse;
        })
      );
      appointmentResponses = [...appointmentResponses, ...newResponses];
      startIterator = startIterator.plus({ day: state.apptRequestChunkSize + 1 });
    }

    let appointments: GenericArrayOfObjects = [];
    let errors = false;
    appointmentResponses.forEach(response => {
      if (flashErrorsIfExist(response)) {
        errors = true;
      }
      if (state.useTestEmails) {
        response.data = response.data.map(appointment => {
          if (!cleanWhiteSpace(appointment.email)) {
            appointment.email = 'placeholder';
          }

          appointment.email = getTestEmail(appointment.email);
          const newEmails = getTestEmails(appointment.ccEmails);
          appointment.ccEmails = newEmails.join(',');

          return appointment;
        });
      }

      appointments = [...appointments, ...response.data];
    });

    if (errors) {
      abortExportProcess();
      return false;
    }

    exportedData.appointments = appointments as INovaAppointment[];
  } catch (error: any) {
    systemNotify({
      message: error.message,
      type: 'error'
    });
    abortExportProcess();
    return false;
  }

  exportWorkbook(exportedData, `${APIs.AP} export - ${state.userName}`);
  state.loading = false;
  state.warehouses = [];
  progress.clear();
  await progress.zoom(99, 5);
  progress.update('All done!', 100);
  setTimeout(() => {
    progress.setDialogVisibility(false);
    progress.update('', 0);
  }, 2000);
};

const getTestEmails = (originalEmails: string) => {
  let emails: string[] = originalEmails.split(EMAIL_SEPARATION_REGEX);
  const newEmails: string[] = [];
  if (emails.length > 0 && emails[0]) {
    emails.forEach((email: string) => {
      newEmails.push(getTestEmail(email));
    });
  }
  return newEmails;
};

const getTestEmail = (email: string) => {
  const emailParts = email.split('@');
  return `${emailParts[0]}${TESTING_EMAIL_DOMAIN}`;
};

const abortExportProcess = () => {
  state.loading = false;
  state.shouldShowDialog = false;
  progress.setDialogVisibility(false);
  progress.clear();
  progress.update('', 0);
};

const close = (shouldClearWarehouses = true) => {
  state.shouldShowDialog = false;
  if (shouldClearWarehouses) {
    state.warehouses = [];
  }
};

const loadWarehouses = async () => {
  const apiService = getClient({
    api: APIs.AP,
    credentials: {
      userName: state.userName.trim(),
      password: state.password.trim()
    },
    url: state.selectedUrl
  }) as AppointmentPlusApiService;
  state.loading = true;
  const warehouseResponse: INormalizedResponse = await apiService.getWarehouses({}, false);
  state.loading = false;
  if (flashErrorsIfExist(warehouseResponse)) {
    abortExportProcess();
    return false;
  }
  state.warehouses = warehouseResponse.data;
  state.shouldShowDialog = true;
};

onMounted(() => {
  startDate.value = DateTime.now().plus({ day: APPT_RANGE_START_DAYS_FROM_NOW }).toJSDate();
  endDate.value = DateTime.now().plus({ day: APPT_RANGE_END_DAYS_FROM_NOW }).toJSDate();
});

// VALIDATION
const fieldErrors = reactive({
  userName: '',
  password: '',
  apptRequestChunkSize: ''
});
const errorMessages = {
  userName: 'A Site ID is required',
  password: 'A Web Services API Key is required',
  apptRequestChunkSize: 'Chunk size must be a positive whole number less than or equal to 365'
};
const isValid = () => {
  if (!state.userName) {
    fieldErrors.userName = errorMessages.userName;
  }
  if (!state.password) {
    fieldErrors.password = errorMessages.password;
  }
  if (!state.apptRequestChunkSize) {
    fieldErrors.apptRequestChunkSize = errorMessages.apptRequestChunkSize;
  }
  if (fieldErrors.userName || fieldErrors.password) {
    return false;
  }
  if (fieldErrors.apptRequestChunkSize) {
    return false;
  }

  return true;
};

watch(
  state,
  newState => {
    if (newState.userName) {
      fieldErrors.userName = '';
    } else {
      fieldErrors.userName = errorMessages.userName;
    }
    if (newState.password) {
      fieldErrors.password = '';
    } else {
      fieldErrors.password = errorMessages.password;
    }
    if (newState.apptRequestChunkSize) {
      if (newState.apptRequestChunkSize < 1 || newState.apptRequestChunkSize > 365) {
        fieldErrors.apptRequestChunkSize = errorMessages.apptRequestChunkSize;
      } else {
        fieldErrors.apptRequestChunkSize = '';
      }
    } else {
      fieldErrors.apptRequestChunkSize = errorMessages.apptRequestChunkSize;
    }
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_switch = _resolveComponent("v-switch")!
  const _component_v_expansion_panel_text = _resolveComponent("v-expansion-panel-text")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_expansion_panel = _resolveComponent("v-expansion-panel")!
  const _component_v_expansion_panels = _resolveComponent("v-expansion-panels")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_v_text_field, {
      modelValue: state.userName,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.userName) = $event)),
      required: "",
      variant: "outlined",
      density: "compact",
      "error-messages": fieldErrors.userName,
      "onUpdate:error-messages": _cache[1] || (_cache[1] = ($event: any) => ((fieldErrors.userName) = $event)),
      class: _normalizeClass({ text__error: fieldErrors.userName }),
      "prepend-inner-icon": "mdi-database",
      label: "Site ID"
    }, null, 8, ["modelValue", "error-messages", "class"]),
    _createVNode(_component_v_text_field, {
      modelValue: state.password,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((state.password) = $event)),
      required: "",
      class: _normalizeClass({ text__error: fieldErrors.password }),
      "error-messages": fieldErrors.password,
      "onUpdate:error-messages": _cache[3] || (_cache[3] = ($event: any) => ((fieldErrors.password) = $event)),
      variant: "outlined",
      density: "compact",
      "prepend-inner-icon": "mdi-key",
      label: "WS API Key"
    }, null, 8, ["modelValue", "class", "error-messages"]),
    _createVNode(_component_v_switch, {
      class: "mt-0",
      modelValue: state.useTestEmails,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((state.useTestEmails) = $event)),
      label: "Use @example for all carrier emails",
      color: "secondary",
      "hide-details": ""
    }, null, 8, ["modelValue"]),
    _createVNode(_component_v_expansion_panels, { class: "my-4" }, {
      default: _withCtx(() => [
        _createVNode(_component_v_expansion_panel, { title: "Advanced Options" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_expansion_panel_text, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_switch, {
                  class: "pt-0 mt-0",
                  modelValue: state.addCustomStatusTag,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((state.addCustomStatusTag) = $event)),
                  label: "Add custom statuses as a tag",
                  color: "secondary",
                  "hide-details": ""
                }, null, 8, ["modelValue"]),
                _hoisted_1
              ]),
              _: 1
            }),
            _createVNode(_component_v_expansion_panel_text, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_select, {
                  class: "mb-6",
                  "persistent-hint": "",
                  hint: "This is to allow for selection of a secondary Legacy URL if the dedicated Opendock URL does not work properly.",
                  required: "",
                  "use-object": "",
                  density: "compact",
                  label: "Select Web Services URL",
                  variant: "outlined",
                  items: _unref(appointmentPlusUrls),
                  modelValue: state.selectedUrl,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((state.selectedUrl) = $event))
                }, null, 8, ["items", "modelValue"]),
                _createVNode(_component_v_text_field, {
                  class: _normalizeClass(["mb-6", { text__error: fieldErrors.apptRequestChunkSize }]),
                  modelValue: state.apptRequestChunkSize,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((state.apptRequestChunkSize) = $event)),
                  required: "",
                  type: "number",
                  "error-messages": fieldErrors.apptRequestChunkSize,
                  "onUpdate:error-messages": _cache[8] || (_cache[8] = ($event: any) => ((fieldErrors.apptRequestChunkSize) = $event)),
                  variant: "outlined",
                  density: "compact",
                  "persistent-hint": "",
                  hint: "Legacy has issues fetching large amounts of appointments.  This setting breaks up each request into x amount of days at a time so you have more control if there is a failure",
                  label: "Appointment Request Chunk Size (in Days)"
                }, null, 8, ["modelValue", "class", "error-messages"]),
                _hoisted_2,
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, [
                        _createVNode(_unref(VueDatePicker), {
                          name: "start-date-picker",
                          "auto-apply": "",
                          "enable-time-picker": false,
                          "month-change-on-scroll": false,
                          "text-input-options": {
                      enterSubmit: true,
                      tabSubmit: true,
                      format: 'MM/dd/yyyy'
                    },
                          class: "date-picker",
                          modelValue: startDate.value,
                          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((startDate).value = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _hoisted_7
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("div", _hoisted_11, [
                        _createVNode(_unref(VueDatePicker), {
                          name: "end-date-picker",
                          "auto-apply": "",
                          "enable-time-picker": false,
                          "month-change-on-scroll": false,
                          "text-input-options": {
                      enterSubmit: true,
                      tabSubmit: true,
                      format: 'MM/dd/yyyy'
                    },
                          class: "date-picker",
                          modelValue: endDate.value,
                          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((endDate).value = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _hoisted_12
                    ])
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_13, [
      _createVNode(WarehouseSelectDialog, {
        warehouses: state.warehouses,
        "is-dialog-visible": state.shouldShowDialog,
        onLoadWarehouses: loadWarehouses,
        loading: state.loading,
        onExportData: exportData,
        onClose: close
      }, null, 8, ["warehouses", "is-dialog-visible", "loading"])
    ]),
    _createVNode(ProgressDialog, {
      title: "Exporting Data From AppointmentPlus",
      "should-show-progress-dialog": progress.shouldShowProgressDialog,
      "current-step-message": progress.currentStepMessage,
      "current-perc": progress.currentPerc
    }, null, 8, ["should-show-progress-dialog", "current-step-message", "current-perc"])
  ]))
}
}

})